import { uuidV4 } from '@animoto/utils/user';

// TODO: RA-564 - remove this page-level reporter once the player itself reports views.

const HEARTBEAT_MS = 30000;
// Headroom for timeupdate jitter. Real jumps are caught by the seeking guard or the rate bound.
const SAMPLE_SLACK_MS = 500;

function createSessionId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return uuidV4().toLowerCase();
}

function guard(fn) {
  return (...args) => {
    try {
      fn(...args);
    } catch (e) {
      // A reporter bug must never break playback.
    }
  };
}

// A string body makes sendBeacon use text/plain, which is CORS-simple: no preflight at pagehide.
// sendBeacon returns false when the browser will not queue the beacon (a full unload queue, an
// oversized body), so that falls through to fetch too.
const send = guard((endpoint, payload) => {
  const body = JSON.stringify(payload);
  if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(endpoint, body)) {
    return;
  }
  if (typeof window.fetch !== 'function') {
    return;
  }
  window.fetch(endpoint, {
    body,
    headers   : { 'Content-Type' : 'text/plain;charset=UTF-8' },
    keepalive : true,
    method    : 'POST'
  }).catch(() => {});
});

function readMedia(player, method) {
  const media = player.player;
  return media && typeof media[method] === 'function' ? media[method]() : undefined;
}

function readPositionMs(player) {
  const seconds = readMedia(player, 'currentTime');
  return Number.isFinite(seconds) ? seconds * 1000 : null;
}

/**
 * Reports one page view of one video to client_service's video_views beacon, following the
 * RA-563 flush rules. watch_ms comes from media positions, so 60s watched at 2x counts 60000.
 *
 * @param {Object} player - Animoto player wrapper: on/off pass-throughs, its video.js instance as `player`, and `cta`
 * @param {Object} [config] - { endpoint, vk, rk, surface, channel }, present only when viewer-analytics-capture is on
 * @returns {Function|null} detaches the reporter, or null when nothing was attached
 */
export default function attachViewAnalyticsReporter(player, config) {
  if (!player || !config) {
    return null;
  }

  const { endpoint } = config;
  const identity = {
    channel : config.channel,
    rk      : config.rk,
    sid     : createSessionId(),
    surface : config.surface,
    v       : 1,
    vk      : config.vk
  };
  const totals = {
    cta_clicks  : 0,
    furthest_ms : 0,
    plays       : 0,
    watch_ms    : 0
  };
  let baseline = null;
  let heartbeat = null;
  let isPlaybackStarted = false;
  let seq = 0;

  const setBaseline = () => {
    const positionMs = readPositionMs(player);
    baseline = positionMs === null ? null : { atMs : Date.now(), positionMs };
  };

  const sample = () => {
    const positionMs = readPositionMs(player);
    // seeking() turns true when currentTime is set, before the 'seeking' event dispatches, so a
    // timeupdate queued in between cannot count the jump. 'seeking' also clears the baseline.
    if (readMedia(player, 'seeking') === true || positionMs === null) {
      return;
    }
    const atMs = Date.now();
    if (baseline) {
      const rate = readMedia(player, 'playbackRate');
      const safeRate = Number.isFinite(rate) && rate > 0 ? rate : 1;
      const deltaMs = positionMs - baseline.positionMs;
      if (deltaMs > 0 && deltaMs <= ((atMs - baseline.atMs) * safeRate) + SAMPLE_SLACK_MS) {
        totals.watch_ms += deltaMs;
        totals.furthest_ms = Math.max(totals.furthest_ms, positionMs);
      }
    }
    baseline = { atMs, positionMs };
  };

  const flush = () => {
    sample();
    seq += 1;
    const payload = {
      ...identity,
      cta_clicks  : totals.cta_clicks,
      furthest_ms : Math.round(totals.furthest_ms),
      plays       : totals.plays,
      seq,
      t           : 'flush',
      watch_ms    : Math.round(totals.watch_ms)
    };
    const durationSeconds = readMedia(player, 'duration');
    if (Number.isFinite(durationSeconds) && durationSeconds > 0) {
      payload.len_ms = Math.round(durationSeconds * 1000);
    }
    send(endpoint, payload);
  };

  const stopHeartbeat = () => {
    clearInterval(heartbeat);
    heartbeat = null;
  };

  const handlers = {
    ended : () => {
      stopHeartbeat();
      flush();
      isPlaybackStarted = false; // so a replay counts as a new play
    },
    loadstart : () => { baseline = null; }, // video.js fires this on every source change
    pause     : () => {
      stopHeartbeat();
      flush();
    },
    play : () => {
      if (!isPlaybackStarted) {
        isPlaybackStarted = true;
        totals.plays += 1;
      }
      setBaseline();
      flush();
      stopHeartbeat();
      heartbeat = setInterval(guard(flush), HEARTBEAT_MS);
    },
    seeked : () => {
      setBaseline();
      flush();
    },
    seeking    : () => { baseline = null; },
    timeupdate : sample,
    waiting    : setBaseline
  };
  const guardedHandlers = Object.keys(handlers).map((event) => [event, guard(handlers[event])]);
  const ctaElement = player.cta && player.cta.ctaElement;
  const onCtaClick = guard(() => { totals.cta_clicks += 1; });
  const onPageHide = guard(flush);

  const detach = guard(() => {
    stopHeartbeat();
    guardedHandlers.forEach(([event, handler]) => player.off(event, handler));
    window.removeEventListener('pagehide', onPageHide);
    if (ctaElement) {
      ctaElement.removeEventListener('click', onCtaClick);
    }
  });

  try {
    const loadPayload = { ...identity, t : 'load' };
    const autoplay = readMedia(player, 'autoplay');
    const isMuted = readMedia(player, 'muted');
    if (autoplay !== undefined) {
      loadPayload.autoplay = Boolean(autoplay); // video.js may report 'any' or 'muted'
    }
    if (typeof isMuted === 'boolean') {
      loadPayload.muted = isMuted;
    }
    send(endpoint, loadPayload);

    guardedHandlers.forEach(([event, handler]) => player.on(event, handler));
    window.addEventListener('pagehide', onPageHide);
    if (ctaElement) {
      ctaElement.addEventListener('click', onCtaClick);
    }
  } catch (e) {
    detach();
    return null;
  }

  return detach;
}
